import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChartBar, Exam, CheckCircle, XCircle } from "@phosphor-icons/react";
import api from "../lib/api";

function scoreColor(p) {
  if (p >= 70) return "text-[#059669] bg-[#059669]/10 border-[#059669]/20";
  if (p >= 50) return "text-accent bg-accent/10 border-accent/20";
  return "text-destructive bg-destructive/10 border-destructive/20";
}

function fmt(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function MyResults() {
  const [results, setResults] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/results/me").then((res) => setResults(res.data.results));
  }, []);

  if (!results) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-muted-foreground">Loading...</div>;

  const best = results.length ? Math.max(...results.map((r) => r.percentage)) : null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12 lg:py-16">
      <div className="flex items-center gap-2">
        <ChartBar size={22} weight="duotone" className="text-accent" />
        <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Your progress</p>
      </div>
      <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight text-foreground mt-2">My Results</h1>

      {results.length === 0 ? (
        <div className="mt-10 rounded-2xl bg-white border border-border p-10 text-center">
          <Exam size={40} weight="duotone" className="mx-auto text-muted-foreground" />
          <p className="text-foreground font-medium mt-4">You haven't taken the test yet.</p>
          <p className="text-muted-foreground text-sm mt-1">Take the test to see how you do.</p>
          <Link
            to="/test"
            className="inline-flex items-center gap-2 mt-6 h-11 px-6 rounded-full bg-primary text-white font-semibold hover:-translate-y-0.5 transition-transform duration-200"
          >
            Take the Test
          </Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 mt-8">
            <div className="rounded-xl bg-white border border-border p-5">
              <p className="text-sm text-muted-foreground">Attempts</p>
              <p className="font-heading font-black text-3xl text-foreground mt-1">{results.length}</p>
            </div>
            <div className="rounded-xl bg-white border border-border p-5">
              <p className="text-sm text-muted-foreground">Best score</p>
              <p className="font-heading font-black text-3xl text-primary mt-1">{best}%</p>
            </div>
          </div>

          <div className="mt-8 space-y-3">
            {results.map((r, i) => {
              const open = expanded === r.id;
              return (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.03 }}
                  className="rounded-xl bg-white border border-border overflow-hidden"
                  data-testid={`result-row-${i}`}
                >
                  <button
                    onClick={() => setExpanded(open ? null : r.id)}
                    className="w-full flex items-center justify-between gap-4 p-5 hover:bg-muted/40 transition-colors duration-200"
                  >
                    <div className="text-left">
                      <p className="font-medium text-foreground">{fmt(r.submitted_at)}</p>
                      <p className="text-sm text-muted-foreground">{r.score} of {r.total} correct</p>
                    </div>
                    <span className={`font-heading font-bold text-lg rounded-full border px-4 py-1 ${scoreColor(r.percentage)}`}>
                      {r.percentage}%
                    </span>
                  </button>
                  {open && (
                    <div className="px-5 pb-5 space-y-2 border-t border-border pt-4">
                      {r.details.map((d) => (
                        <div key={d.question_id} className="flex items-start gap-2 text-sm">
                          {d.correct ? (
                            <CheckCircle size={18} weight="fill" className="text-[#059669] shrink-0 mt-0.5" />
                          ) : (
                            <XCircle size={18} weight="fill" className="text-destructive shrink-0 mt-0.5" />
                          )}
                          <span className="text-foreground">{d.question}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
